import {
  AdditionalBuilding,
  AdditionalBuildingStandard,
  AdditionalBuildingType,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/AdditionalBuildings/additionalBuildingsInterfaces";
import {
  CABIN_ABOUT_STEP,
  CABIN_PRICE_STEP,
} from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/Cabin";
import {
  booleanToFormBoolean,
  nullableBooleanToNullableFormBoolean,
} from "Utils/formUtils";

import { CABIN_INTRO_STEP } from "Components/Features/CheckPrice/Products/RiskProducts/HouseProducts/Cabin/cabinSteps";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { FormStates } from "Store/priceCalculation/priceCalculationInterfaces";
import { mapInjuriesToForm } from "Store/priceCalculation/productMappers/common/injuriesMapper";
import { createKey } from "Utils/generalUtils";
import { DEDUCTIBLE_FOUR_THOUSAND } from "Utils/priceUtils";
import { DEFAULT_DEDUCTIBLE_CABIN } from "../cabinConstants";

interface MappedAdditionalBuilding {
  constructionYear: string;
  coverage: string;
  grossArea: number;
  type: string;
  isIsolated?: boolean;
  livingFloorSpace?: number;
  hasTapWater?: boolean;
  hasElectricity?: boolean;
  standard?: string;
}

export function mapFromCommerceCabin(data: { [key: string]: any }): FormStates {
  const mapAdditionalBuildings = (
    buildings: MappedAdditionalBuilding[],
  ): AdditionalBuilding[] =>
    buildings.map((building) => {
      return {
        id: createKey(),
        type: building.type,
        areal: `${building.grossArea}`,
        constructionYear: building.constructionYear,
        isolated: building.isIsolated ?? undefined,
        ...(building.type === AdditionalBuildingType.ANNEX && {
          boa: `${building.livingFloorSpace}`,
          hasTapWater: building.hasTapWater,
          hasElectricity: building.hasElectricity,
          standard:
            building.standard === AdditionalBuildingStandard.NORMAL
              ? AdditionalBuildingStandard.NORMAL
              : AdditionalBuildingStandard.HIGH,
        }),
      };
    });

  const deductibleCode = String(data.priceSpecifications.deductible.code);

  return {
    [CABIN_INTRO_STEP.id]: {
      postNumber: data.address?.zipCode,
      validPostNumber: true,
      city: data.address?.city,
      constructionYear: `${data.constructionYear}`,
      grossArea: `${data.grossArea}`,
      livingArea: `${data.livingFloorSpace}`,
      otherBuildings: data.additionalBuildings
        ? mapAdditionalBuildings(data.additionalBuildings)
        : [],
    },
    [CABIN_ABOUT_STEP.id]: {
      standard: data.standard,
      historicalValue: BooleanFormType.FALSE,
      hasRental: nullableBooleanToNullableFormBoolean(data.hasRental),
      rentalTimeExceedsOneMonthAYear: BooleanFormType.FALSE,
      hasElectricity: booleanToFormBoolean(data.hasElectricity),
      hasTapWater: booleanToFormBoolean(data.hasTapWater),
      hasCrawlspace: booleanToFormBoolean(data.crawlspace),
      pestsDamaged: booleanToFormBoolean(data.previousFungusDamage),
      ...mapInjuriesToForm(data.damageCountRecentYears, data.yearOfLastDamage),
    },
    [CABIN_PRICE_STEP.id]: {
      coverage: data.coverageGroup,
      deductible:
        deductibleCode === DEDUCTIBLE_FOUR_THOUSAND
          ? DEFAULT_DEDUCTIBLE_CABIN
          : deductibleCode,
      addContentsInsurance: BooleanFormType.FALSE,
    },
  };
}
